/**
 * What rotki v1.44.1 reads, generated by `pnpm sync:rotki` from rotki's source. Do not edit by
 * hand: see "Updating to a new rotki release" in the README.
 */
export const ROTKI_RELEASE = 'v1.44.1';

/**
 * Locations rotki deserializes, as `Location.serialize()` writes them: the enum name in lower
 * case with spaces for underscores. rotki files any other value under `external` without a word,
 * so the converter rejects those rows instead.
 */
export const ROTKI_LOCATIONS = [
  'external',
  'kraken',
  'poloniex',
  'bittrex',
  'binance',
  'bitmex',
  'coinbase',
  'banks',
  'blockchain',
  'coinbasepro',
  'gemini',
  'equities',
  'realestate',
  'commodities',
  'cryptocom',
  'uniswap',
  'bitstamp',
  'binanceus',
  'bitfinex',
  'bitcoinde',
  'iconomi',
  'kucoin',
  'balancer',
  'loopring',
  'ftx',
  'nexo',
  'blockfi',
  'independentreserve',
  'gitcoin',
  'sushiswap',
  'shapeshift',
  'uphold',
  'bitpanda',
  'bisq',
  'ftxus',
  'okx',
  'ethereum',
  'optimism',
  'polygon pos',
  'arbitrum one',
  'base',
  'gnosis',
  'woo',
  'bybit',
  'scroll',
  'zksync lite',
  'htx',
  'bitcoin',
  'bitcoin cash',
  'polkadot',
  'kusama',
  'coinbaseprime',
  'binance sc',
  'solana',
  'avalanche',
  'hyperliquid',
  'monad',
  'gate',
  'bit2me',
  'coinex',
] as const;

/** Values rotki accepts in the `Type` column of a generic events file. */
export const ROTKI_EVENT_TYPES = [
  'Deposit',
  'Withdrawal',
  'Income',
  'Loss',
  'Spend',
  'Staking',
] as const;

/**
 * The columns each of rotki's CSV importers reads, by importer module in
 * `rotkehlchen/data_import/importers`. An importer that handles several layouts lists the
 * columns of all of them.
 */
export const ROTKI_IMPORTER_COLUMNS = {
  binance: [
    'Account',
    'Change',
    'Coin',
    'Operation',
    'Remark',
    'UTC_Time',
  ],
  bisq_trades: [
    'Amount',
    'Amount in BTC',
    'Date/Time',
    'Market',
    'Offer type',
    'Price',
    'Status',
    'Trade Fee BSQ',
    'Trade Fee BTC',
    'Trade ID',
  ],
  bitcoin_tax: [
    'Account',
    'Action',
    'Cost/Proceeds',
    'Currency',
    'Date',
    'Fee',
    'FeeCurrency',
    'Memo',
    'Symbol',
    'Volume',
  ],
  bitmex: [
    'address',
    'amount',
    'fee',
    'transactStatus',
    'transactTime',
    'transactType',
  ],
  bitstamp: [
    'Amount',
    'Datetime',
    'Fee',
    'Sub Type',
    'Type',
    'Value',
  ],
  bittrex: [
    'Address',
    'ADDRESS',
    'Amount',
    'AMOUNT',
    'Base',
    'Closed',
    'CLOSED',
    'CryptoAddress',
    'CRYPTOADDRESS',
    'Currency',
    'CURRENCY',
    'Date',
    'Exchange',
    'FILLED',
    'LastUpdated',
    'LASTUPDATED',
    'LIMIT',
    'MARKET',
    'Opened',
    'ORDERTYPE',
    'OrderUuid',
    'Price',
    'Quantity',
    'Quantity (Base)',
    'Quote',
    'QUOTE',
    'Time (UTC)',
    'Transaction',
    'TxId',
    'TXID',
    'Type',
    'UUID',
  ],
  blockfi_trades: [
    'Buy Currency',
    'Buy Quantity',
    'Date',
    'Sold Currency',
    'Sold Quantity',
    'Trade ID',
    'Type',
  ],
  blockfi_transactions: [
    'Amount',
    'Confirmed At',
    'Cryptocurrency',
    'Transaction Type',
  ],
  blockpit: [
    'Fee Amount',
    'Fee Asset',
    'Incoming Amount',
    'Incoming Asset',
    'Note',
    'Outgoing Amount',
    'Outgoing Asset',
    'Source Name',
    'Timestamp',
    'Transaction Type',
  ],
  coinbasepro: [
    'Amount',
    'Amount/balance unit',
    'Timestamp',
    'Trade id',
    'Type',
  ],
  coinledger: [
    'Account Name',
    'Amount',
    'Asset',
    'Blockchain Id',
    'Description',
    'Internal Id',
    'Platform',
    'Platform Id',
    'Record Type',
    'Timestamp (UTC)',
    'Type',
  ],
  cointracking: [
    'Buy',
    'Comment',
    'Cur.Buy',
    'Cur.Fee',
    'Cur.Sell',
    'Date',
    'Exchange',
    'Fee',
    'Sell',
    'Type',
  ],
  cryptocom: [
    'Amount',
    'Currency',
    'Native Amount',
    'Native Amount (in USD)',
    'Native Currency',
    'Timestamp (UTC)',
    'To Amount',
    'To Currency',
    'Transaction Description',
    'Transaction Kind',
  ],
  kucoin: [
    'Amount',
    'AmountCoin',
    'fee',
    'Fee',
    'FeeCoin',
    'feeCurrency',
    'FilledPrice',
    'FilledPriceCoin',
    'funds',
    'Pair',
    'price',
    'side',
    'Side',
    'size',
    'symbol',
    'Time',
    'tradeCreatedAt',
    'Volume',
    'VolumeCoin',
  ],
  nexo: [
    'Date / Time (UTC)',
    'Details',
    'Input Amount',
    'Input Currency',
    'Output Amount',
    'Output Currency',
    'Transaction',
    'Type',
  ],
  rotki_events: [
    'Amount',
    'Currency',
    'Description',
    'Fee',
    'Fee Currency',
    'Location',
    'Timestamp',
    'Type',
  ],
  rotki_trades: [
    'Description',
    'Fee',
    'Fee Currency',
    'Location',
    'Receive Amount',
    'Receive Currency',
    'Spend Amount',
    'Spend Currency',
    'Timestamp',
  ],
  shapeshift_trades: [
    'inputAddress',
    'inputAmount',
    'inputCurrency',
    'inputTxid',
    'minerFee',
    'outputAddress',
    'outputAmount',
    'outputCurrency',
    'outputTxid',
    'rate',
    'refundAddress',
    'refundTxid',
    'timestamp',
  ],
  uphold_transactions: [
    'Date',
    'Destination',
    'Destination Amount',
    'Destination Currency',
    'Fee Amount',
    'Fee Currency',
    'Id',
    'Origin',
    'Origin Amount',
    'Origin Currency',
    'Status',
    'Type',
  ],
} as const;
